<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import LossReasonSelect from './LossReasonSelect.vue';

// Modal de cierre (paridad Kommo mejorada): toda transición a una etapa
// cerrada desde el tablero pasa por acá — al perder pide el motivo (requerido
// si hay lista configurada; gate real en el modelo → 422), al ganar confirma
// el valor final de la venta (el widget "Ganado" refleja plata real).
const props = defineProps({
  reasons: { type: Array, default: () => [] },
});
const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const stage = ref(null);
const lead = ref(null);
const lostReason = ref('');
const finalValue = ref('');
const isSubmitting = ref(false);

const isLost = computed(() => stage.value?.stage_type === 'lost');
const reasonRequired = computed(() => isLost.value && props.reasons.length > 0);
const canConfirm = computed(
  () => !reasonRequired.value || !!lostReason.value.trim()
);

const open = (targetLead, targetStage) => {
  lead.value = targetLead;
  stage.value = targetStage;
  lostReason.value = '';
  finalValue.value =
    targetLead.value !== null && targetLead.value !== undefined
      ? String(targetLead.value)
      : '';
  isSubmitting.value = false;
  dialogRef.value.open();
};
const close = () => dialogRef.value?.close();

const onConfirm = () => {
  if (!canConfirm.value || isSubmitting.value) return;
  isSubmitting.value = true;
  const extras = isLost.value
    ? { lost_reason: lostReason.value.trim() }
    : { value: finalValue.value === '' ? null : Number(finalValue.value) };
  if (extras.value === null) delete extras.value;
  emit('confirm', { lead: lead.value, stage: stage.value, extras });
};

// El padre decide cuándo cerrar (éxito) o re-habilitar (422). Cancelar/Esc/
// click-afuera emite close → el padre revierte el drag si lo hubo.
const settle = ok => {
  isSubmitting.value = false;
  if (ok) close();
};

defineExpose({ open, close, settle });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="isLost ? t('LEADS.CLOSE.LOST_TITLE') : t('LEADS.CLOSE.WON_TITLE')"
    :description="
      isLost
        ? t('LEADS.CLOSE.LOST_DESCRIPTION', { stage: stage?.name || '' })
        : t('LEADS.CLOSE.WON_DESCRIPTION', { stage: stage?.name || '' })
    "
    :confirm-button-label="
      isLost ? t('LEADS.CLOSE.CONFIRM_LOST') : t('LEADS.CLOSE.CONFIRM_WON')
    "
    :is-loading="isSubmitting"
    :disable-confirm-button="!canConfirm || isSubmitting"
    @confirm="onConfirm"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-3">
      <label v-if="isLost" class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('LEADS.CLOSE.REASON_LABEL') }}
          <span v-if="reasonRequired" class="text-n-ruby-11">*</span>
        </span>
        <LossReasonSelect v-model="lostReason" :reasons="reasons" />
      </label>
      <label v-else class="flex flex-col gap-1.5">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('LEADS.CLOSE.VALUE_LABEL') }}
        </span>
        <Input
          v-model="finalValue"
          type="number"
          :placeholder="t('LEADS.CLOSE.VALUE_PLACEHOLDER')"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('LEADS.CLOSE.VALUE_HINT') }}
        </span>
      </label>
    </div>
  </Dialog>
</template>
